import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/shared/SectionHeading";
import { LoadingGrid, ErrorState, EmptyState } from "@/components/shared/DataState";
import { useAsyncData } from "@/hooks/useAsyncData";
import { getBlogs } from "@/data/repository";
import { Search, ArrowUpRight } from "lucide-react";

function BlogCard({ b, idx }) {
  return (
    <Link
      to={`/resources/blogs/${b.slug || b.id}`}
      data-testid={`blog-card-${b.slug || b.id}`}
      className="reveal group cursor-pointer flex flex-col border border-bronze/20 bg-cream overflow-hidden"
      style={{ transitionDelay: `${idx * 60}ms` }}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={b.image}
          alt={b.title}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
          loading="lazy"
        />
        <span className="absolute top-4 left-4 bg-cream/90 backdrop-blur-sm text-bronze text-[0.65rem] tracking-[0.22em] uppercase px-3 py-1">
          {b.category}
        </span>
      </div>
      <div className="flex-1 p-7 flex flex-col">
        <div className="flex items-center gap-3 text-[0.7rem] tracking-[0.22em] uppercase text-olive">
          <span>{b.date}</span><span className="text-bronze">·</span><span>{b.readTime}</span>
        </div>
        <h3 className="mt-3 font-serif text-2xl text-ink leading-snug">{b.title}</h3>
        <p className="mt-3 text-sm font-light leading-relaxed text-ink_soft/80">{b.excerpt}</p>
        <span className="mt-auto pt-6 inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze group-hover:text-bronze_dark">
          Read article <ArrowUpRight size={14} />
        </span>
      </div>
    </Link>
  );
}

export default function BlogsSection({ withHeading = true }) {
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const { data: blogs, loading, error, reload } = useAsyncData(getBlogs, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(blogs.map((b) => b.category).filter(Boolean)))],
    [blogs]
  );

  const filteredBlogs = useMemo(() => {
    const q = query.toLowerCase();
    return blogs.filter(
      (b) =>
        (category === "All" || b.category === category) &&
        (b.title.toLowerCase().includes(q) || b.excerpt.toLowerCase().includes(q))
    );
  }, [blogs, category, query]);

  return (
    <section data-testid="blog-section" className="section-pad bg-cream">
      <div className="container-page">
        {withHeading && (
          <SectionHeading
            eyebrow="Journal"
            title={<>Insights from the <span className="italic text-bronze">ground floor.</span></>}
            sub="Field notes, strategies, and quiet observations from working with Singapore homeowners and investors."
          />
        )}

        <div className={`${withHeading ? "mt-12" : ""} flex flex-col md:flex-row gap-4 md:items-center md:justify-between`}>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 text-[0.65rem] tracking-[0.22em] uppercase border transition-colors ${
                  category === c
                    ? "bg-bronze text-cream border-bronze"
                    : "border-bronze/30 text-ink_soft hover:border-bronze hover:text-bronze"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative max-w-xs w-full">
            <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-bronze" />
            <input
              type="text"
              placeholder="Search articles..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="blog-search"
              className="editorial-input !pl-8"
            />
          </div>
        </div>

        {loading && <div className="mt-12"><LoadingGrid count={3} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" /></div>}

        {!loading && error && <ErrorState onRetry={reload} />}

        {!loading && !error && (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {filteredBlogs.map((b, i) => (
              <BlogCard key={b.id} b={b} idx={i} />
            ))}
          </div>
        )}

        {!loading && !error && filteredBlogs.length === 0 && (
          <EmptyState title="No articles match your search." />
        )}
      </div>
    </section>
  );
}
